import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { UserCircle2, X, ChevronDown, Check } from "lucide-react";

/**
 * Sticky "Serving: [Name]" banner on Take Stock.
 *
 * Reception picks a person once (e.g. "Bec at desk") and every
 * subsequent quick-take (-1 / -5 buttons + numpad) inherits the name
 * without having to retype it. Clearing the banner drops back to
 * per-take entry via the RequesterCombobox in the numpad footer.
 *
 * Value is persisted in sessionStorage by the parent so a page
 * refresh keeps the desk sticky, and it clears on logout.
 */
const ServingBanner = ({ value, onChange }) => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    api
      .get("/requesters")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setTyped("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const q = typed.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      rows
        .filter((r) => (!q ? true : r.name.toLowerCase().includes(q)))
        .slice(0, 25),
    [rows, q],
  );

  const commit = (name) => {
    onChange(name.trim());
    setOpen(false);
    setTyped("");
  };

  // Active state — the yellow "Serving X" chip with a Clear button.
  if (value) {
    return (
      <div
        className="mb-4 flex items-center gap-3 flex-wrap border-2 border-black rounded-xl bg-[#FFF7D6] px-4 py-3"
        data-testid="serving-banner"
      >
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-[#0D1F3C] text-white font-display text-lg flex items-center justify-center border-2 border-black">
            {value[0]?.toUpperCase()}
          </div>
          <div className="leading-tight">
            <div className="label-eyebrow text-[10px]">Serving</div>
            <div
              className="font-display text-xl"
              data-testid="serving-banner-name"
            >
              {value}
            </div>
          </div>
        </div>
        <div className="text-[11px] text-[#404040] flex-1 min-w-[220px]">
          Every quick take will be tagged to <b>{value}</b> until you clear
          this or log out.
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="nb-btn bg-white px-3 py-2 text-xs flex items-center gap-1"
          data-testid="serving-banner-change"
        >
          <ChevronDown size={12} strokeWidth={2.75} /> Change
        </button>
        <button
          type="button"
          onClick={() => commit("")}
          className="nb-btn bg-white px-3 py-2 text-xs flex items-center gap-1"
          data-testid="serving-banner-clear"
        >
          <X size={12} strokeWidth={2.75} /> Clear
        </button>
        {open && (
          <div
            ref={ref}
            className="w-full sm:w-96 mt-2 border-2 border-black rounded-xl bg-white p-3 space-y-2"
          >
            <input
              autoFocus
              className="nb-input w-full"
              placeholder="Search or type a new name…"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && typed.trim()) commit(typed);
              }}
              data-testid="serving-banner-search"
            />
            <div className="max-h-56 overflow-y-auto flex flex-col gap-1">
              {filtered.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => commit(r.name)}
                  className={`nb-btn bg-white px-3 py-2 text-left text-sm flex items-center gap-2 ${
                    r.name === value ? "border-4" : ""
                  }`}
                  data-testid={`serving-banner-option-${r.name}`}
                >
                  {r.name === value && <Check size={14} strokeWidth={2.75} />}
                  {r.name}
                </button>
              ))}
              {typed.trim() &&
                !filtered.some(
                  (r) => r.name.toLowerCase() === q,
                ) && (
                  <button
                    type="button"
                    onClick={() => commit(typed)}
                    className="nb-btn nb-btn-primary px-3 py-2 text-sm text-left"
                    data-testid="serving-banner-add-new"
                  >
                    + Use &ldquo;{typed}&rdquo;
                  </button>
                )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Idle state — small pill-shaped call-to-action.
  return (
    <div
      className="mb-4 relative"
      ref={ref}
      data-testid="serving-banner-idle"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="nb-btn bg-white px-4 py-2.5 flex items-center gap-2 text-sm"
        data-testid="serving-banner-open"
      >
        <UserCircle2 size={16} strokeWidth={2.5} />
        Serving:{" "}
        <span className="text-[#607087]">no one yet — tap to set</span>
      </button>
      {open && (
        <div className="absolute left-0 mt-2 w-full sm:w-96 border-2 border-black rounded-xl bg-white p-3 space-y-2 shadow-lg z-20">
          <div className="text-[11px] text-[#404040]">
            Pick who&apos;s at the desk — every quick take will be tagged
            to them.
          </div>
          <input
            autoFocus
            className="nb-input w-full"
            placeholder="Search or type a new name…"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && typed.trim()) commit(typed);
            }}
            data-testid="serving-banner-search"
          />
          <div className="max-h-56 overflow-y-auto flex flex-col gap-1">
            {filtered.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => commit(r.name)}
                className="nb-btn bg-white px-3 py-2 text-left text-sm"
                data-testid={`serving-banner-option-${r.name}`}
              >
                {r.name}
              </button>
            ))}
            {typed.trim() &&
              !filtered.some((r) => r.name.toLowerCase() === q) && (
                <button
                  type="button"
                  onClick={() => commit(typed)}
                  className="nb-btn nb-btn-primary px-3 py-2 text-sm text-left"
                  data-testid="serving-banner-add-new"
                >
                  + Use &ldquo;{typed}&rdquo;
                </button>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServingBanner;
